import { z } from 'zod';

import { ChatUiMoneySchema } from '../../shared/schema/money_schema.js';
import { ChatUiFlightSegmentSchema } from '../../shared/schema/flight_segment_schema.js';
import { chatUiBase } from '../../shared/schema/base.js';
import { ChatUiMaskedDocumentSchema } from '../../shared/schema/document_schema.js';
import { TravelerRefSchema } from '../../shared/schema/traveler_ref.js';

const ChatUiFlightReservationSchema = z.object({
  // `null` while issuance is async (http mode) — the PNR materializes later.
  locator: z.string().min(1).nullable(),
  status: z.enum(['confirmed', 'pending']),
  price: ChatUiMoneySchema,
  // Reserva multi-viajante: refs determinísticas do checkout + id de quem regeu
  // a política. Ambos opcionais para reidratar históricos do contrato escalar.
  travelers: z.array(TravelerRefSchema).min(1).optional(),
  policyTravelerId: z.string().min(1).optional(),
  // Fallback histórico single-passenger. Em payloads novos, os escalares continuam
  // descrevendo o viajante primário (`travelers[0]`) para compatibilidade.
  passengerName: z.string().optional(),
  flight: ChatUiFlightSegmentSchema.optional(),
  // Perna de VOLTA (ida-e-volta): presente quando a reserva é bundled.
  returnFlight: ChatUiFlightSegmentSchema.optional(),
  // Multi-trecho: UM recibo por trecho do roteiro (≥2 trechos one-way). Quando
  // presente, o card lista TODOS os trechos (cada um com seu status/localizador e
  // preço) e o `price` do topo é o TOTAL do roteiro; `flight`/`returnFlight` ficam
  // ausentes. Ausente ⇒ contrato escalar (single/ida-volta). Aditivo: históricos
  // escalares reidratam sem `legs`.
  legs: z
    .array(
      z.object({
        flight: ChatUiFlightSegmentSchema,
        price: ChatUiMoneySchema,
        status: z.enum(['confirmed', 'pending']),
        locator: z.string().min(1).nullable(),
      }),
    )
    .min(2)
    .optional(),
  // Tarifa base (da cotação do turno) — deriva "Taxas e encargos" = price − baseFare
  // no FlightFareBreakdown; ausente → só Total.
  baseFare: ChatUiMoneySchema.optional(),
  // CPF SEMPRE mascarado (display-only). Hoje preenchido só na pista determinística
  // (handler), via businessApproval.user.maskedCpf; ausente → a linha do CPF degrada.
  document: ChatUiMaskedDocumentSchema.optional(),
  // Foto do destino no header do card. Opcional; não preenchido em PR1 (banda cai
  // no gradiente). Ponto de inserção da plumbing de imagem de destino (PR2).
  destinationImageUrl: z.url().optional(),
  // Recibo corporativo: rótulos selecionados de centro de custo + motivo (ecoados
  // display-only pelo drawer; os ids autorizadores vão em confirmation.selections)
  // e veredito de política da cotação → escudo "Dentro da política". Opcionais.
  costCenter: z.string().optional(),
  motive: z.string().optional(),
  withinPolicy: z.boolean().optional(),
});
export type ChatUiFlightReservation = z.infer<typeof ChatUiFlightReservationSchema>;

/** Membro `flight_reservation` do `ChatUiSchema` (discriminatedUnion). */
export const FlightReservationBlockSchema = z
  .object({
    ...chatUiBase,
    kind: z.literal('flight_reservation'),
    reservation: ChatUiFlightReservationSchema,
  })
  .strict();
